import { useCallback, useMemo } from "react";
import { NEW_EDGE_REG } from "@views/thinking-layout-editor/constants";
import useFeatureTags from "@custom-hooks/useFeatureTags";
import { useStore, useDispatch } from "react-redux";
import { PreviewAppUtil } from "@uiview/views/PreviewApp/PreviewAppUtil";
import { createContainer } from "unstated-next";
import { useSignal } from "@preact/signals-react";
import { PreviewAppTypeMap } from "@uiview/views/PreviewApp/const";
import { studioActions } from "@store/studio";
import { useModal } from "@uiview/views/AModal/useModal";
import { useIsStaticApp } from "@custom-hooks/useNodePreview";
import { getIt } from "@uikit/getIt";
import isBlank from "@sedan-utils/is-blank";
import { previewStore } from "@uiview/store/PreviewStore";
import { useStore as useImagicaStore } from "imagica-uikit/dist/hooks/useStore";
import { CreatorSaasAppStore } from "@uiview/views/ProjectCanvas/store/CreatorSaasAppStore";
import { PreviewAppStore } from "@uiview/views/ProjectCanvas/store/PreviewAppStore";
import { PreviewAppValueGenUtil } from "@uiview/views/PreviewApp/PreviewAppValueGenUtil";
import { PreviewAppValueLangUtil } from "@uiview/views/PreviewApp/PreviewAppValueLangUtil";
import { CreatorNodesStore } from "@uiview/views/ProjectCanvas/store/CreatorNodesStore";

/**
 * 为 saas 预览提供状态和内部实现逻辑
 *
 * 在这之前的版本中， saas 预览都使用的是同一份数据(saasUIData)
 * 在当前版本中，saas 预览可以有多份不同预览数据，在这之前是无法做的
 *
 * 但考虑到前期在使用 saasUIData 时处理逻辑和使用方式过多，以下为解决方法：
 * 1. 增加一个额外的预览数据列表(previewAppList)和 previewAppActiveIdSignal 分别表示预览列表和当前预览项目ID
 * 2. 当切换 previewAppActiveIdSignal 时将指定 previewAppList 中的对应项设置给 saasUIData，从使用角度快速处理多个 preview app 的情况
 *
 *
 * **该文件应该属于一个独立的模块,不应在当前目录,且内部状态应该使用 BaseStore 实现,这些都是第一次在创建时没考虑到的问题**
 * @see https://brain-ai.atlassian.net/browse/STUD-1639
 * @see https://brain-ai.atlassian.net/browse/STUD-1722
 * @returns
 */
// eslint-disable-next-line
function usePreviewApp() {
  const previewAppStore = getIt(PreviewAppStore);
  const creatorNodesStore = getIt(CreatorNodesStore);
  const creatorSaasAppStore = getIt(CreatorSaasAppStore);

  const creatorPreviewState = useImagicaStore(previewStore).value;
  const creatorSaasAppState = useImagicaStore(creatorSaasAppStore).value;
  const previewAppState = useImagicaStore(previewAppStore).value;
  const previewState = useImagicaStore(previewStore).value;
  const featureTags = useFeatureTags();
  const { saasUIData } = creatorSaasAppState;

  // preview panel 显示状态, null 未挂载, false 不显示,true 显示

  const isInHistory = useMemo(() => {
    if (featureTags.enablePublishV3) {
      return (
        previewAppState.historyShowStatus && !previewAppState.publishShowStatus && !previewAppState.previewShowStatus
      );
    }
    return (
      previewAppState.historyShowStatus &&
      !previewAppState.publishShowStatus &&
      !previewAppState.previewShowStatus &&
      !previewAppState.settingShowStatus
    );
  }, [
    previewAppState.historyShowStatus,
    previewAppState.publishShowStatus,
    previewAppState.previewShowStatus,
    previewAppState.settingShowStatus,
  ]);

  const showDeleteTipSignal = useSignal(false);

  const store = useStore();
  const showModal = useModal();
  const dispatch = useDispatch();
  const setSelectedTemplate = (val: PreviewApp.ProjectType): void => {
    dispatch(studioActions.setSelectedTemplate(val));
  };
  const { isStaticApp } = useIsStaticApp();
  const appStates = useMemo(() => {
    const chatApp = PreviewAppValueLangUtil.isChatAiApp(creatorSaasAppStore.state.saasUIData);
    return {
      isChatApp: PreviewAppValueLangUtil.isChatAiApp(creatorSaasAppStore.state.saasUIData),
      chatMode: chatApp
        ? PreviewAppUtil.getChatAiMode({
            saasUIData: creatorSaasAppStore.state.saasUIData,
            nodes: creatorNodesStore.getNodes(),
          })
        : undefined,
    };
  }, [previewState.previewAppAcivieId, saasUIData]);

  const addPreview = useCallback((args: PreviewApp.AddPrviewArgs) => {
    const editorStore = (store.getState() as any).editor;
    const selectTemp = getSelectedTemplate(true);
    if (!selectTemp) {
      return;
    }
    // FIXME: 注意 原来的App被删除后，再次添加会重复。需要保存一次过后，就没有这个问题。
    previewStore.setVDataLastIndex((selectTemp?.lastIndex || previewStore.state.previewAppList.length) + 1);
    const newTarget = PreviewAppValueGenUtil.genPreviewApp(previewAppStore.getSaasUIData(), {
      theme: editorStore.applicationTheme,
      fontSize: editorStore.fontSize,
      // TODO: 老版本使用 redux isStaticMode, v2 使用 type 区分
      staticMode: args.format.appType === PreviewAppTypeMap.STATIC_WEB,
      ...args.uiData,
      v2App: PreviewAppValueGenUtil.genV2App({
        format: args.format,
        lastIndex: previewStore.state.vDataLastIndex,
        lastTypeIndex: PreviewAppValueGenUtil.getAppTypeCountByFormat(previewStore.state.previewAppList, args.format),
      }),
    });

    previewStore.setPreviewAppList([...previewStore.state.previewAppList, newTarget]);

    previewAppStore.previewToSaasUiData(newTarget.id);
    // syncPreviewAppData(newTarget.id)
    return newTarget;
  }, []);

  /**
   * 获取当前项目所有数据，包括 ui, v3, publish
   */
  const getSelectedTemplate = useCallback((clone: boolean = false) => {
    const selectedTemplate = (store.getState() as any).studio.selectedTemplate as PreviewApp.ProjectType | undefined;
    if (selectedTemplate) {
      // FIXME: 当input 中有 function 类型时应该去掉,否则设置为redux时会报错?
      return clone ? { ...selectedTemplate } : selectedTemplate;
    }
    return;
  }, []);

  const updateSelectTemplate = (updateObj: any): void => {
    const selectedTemplate = getSelectedTemplate();
    const updatedSelectedTemplate = Object.assign({}, selectedTemplate, {
      ...updateObj,
    });
    setSelectedTemplate(updatedSelectedTemplate);
  };

  const delPublishedMetadataV2 = (id: string): PreviewApp.PublishedMetadataV2Type[] => {
    const project: any = { ...getSelectedTemplate() };
    // eslint-disable-next-line
    const publishedMetadataV2 = project?.published_metadata_v2 ? [...project?.published_metadata_v2] : [];
    const idx = publishedMetadataV2.findIndex((x: PreviewApp.PublishedMetadataV2Type) => x.id === id);

    if (idx !== -1) {
      publishedMetadataV2.splice(idx, 1);
    }
    return publishedMetadataV2;
  };
  const previewAppActivePublishMetadataV2Item = (): any => {
    const project: any = { ...getSelectedTemplate() };
    // eslint-disable-next-line
    const published_metadata_v2 = project?.published_metadata_v2 ? [...project?.published_metadata_v2] : [];
    return published_metadata_v2.find(
      (x: PreviewApp.PublishedMetadataV2Type) => x.id === previewStore.state.previewAppAcivieId
    );
  };

  const getPreviewAppActiveLable = useCallback(() => {
    const activeItem = previewStore.state.previewAppList.find(
      (x: PreviewApp.UISaasValueType) => x.id === previewStore.state.previewAppAcivieId
    );
    return activeItem?.v2App?.type;
  }, []);

  const removeByNodeIds = useCallback((ids: string[]) => {
    previewStore.setPreviewAppList(PreviewAppUtil.removeByNodeIds(previewStore.state.previewAppList, ids));
  }, []);

  function edgeSourceNodeAsInput(nodeids: string[]): string[] {
    const edgeNodes = nodeids?.filter(id => NEW_EDGE_REG.test(id));

    if (!isBlank(edgeNodes)) {
      const allSourceNodes = creatorNodesStore
        .getNodes()
        .flatMap(node => {
          if (node.data.flows && edgeNodes.includes(node.id)) {
            const flows = node.data.flows as { sourceNodeId: string }[];
            return flows.map(flow => flow.sourceNodeId);
          }
          return "";
        })
        ?.filter(id => !isBlank(id));
      return allSourceNodes;
    }
    return [];
  }

  const triggerRemovePreviewAppNode = useCallback((args: PreviewApp.RemovePreviewAppNodeArgs) => {
    const { ids, edgeNodeIds, onSuccess } = args;

    let removeIds: string[] = [];

    let modalDes = "";
    const edgeSource = edgeSourceNodeAsInput(edgeNodeIds);
    if (
      PreviewAppValueLangUtil.hasNodeIds(
        previewStore.state.previewAppList,
        creatorSaasAppStore.state.saasUIData,
        edgeSource
      )
    ) {
      // 暂时由开发确定为如下文字 后续有变化再改
      modalDes = "This edge sourceNode is currently being used by preview app";
      removeIds = edgeSource;
    }

    if (
      PreviewAppValueLangUtil.hasNodeIds(previewStore.state.previewAppList, creatorSaasAppStore.state.saasUIData, ids)
    ) {
      removeIds = [...edgeSource, ...ids];
      modalDes = "This node is currently being used by preview app";
    }

    if (modalDes) {
      showModal({
        title: "Warning",
        width: 520,
        subTitle: modalDes,
        buttons: ["Cancel", "Confirm"],
        onOk: ({ close }) => {
          onSuccess(removeIds);
          removeByNodeIds(removeIds);
          close();
        },
      });
    } else {
      onSuccess(removeIds);
    }
  }, []);

  const updatePreviewV2App = useCallback((id: any, value: Partial<Omit<PreviewApp.PreviewAppV2FieldType, "id">>) => {
    previewStore.setPreviewAppList(
      previewStore.state.previewAppList.map(item => {
        if (item.id === id) {
          return {
            ...item,
            v2App: {
              // 为了类型安全
              id: item.id as any,
              ...item.v2App,
              ...value,
            },
          };
        }
        return item;
      })
    );
  }, []);

  const deletePreviewV2App = useCallback((id: any) => {
    previewStore.setPreviewAppList(previewStore.state.previewAppList.filter(item => item.id !== id));

    const selectedTemplate = getSelectedTemplate();
    const updatedUISelectedTemplate = previewAppStore.assignPreviewAppData(selectedTemplate);

    updateSelectTemplate({
      ...updatedUISelectedTemplate,
      published_metadata_v2: delPublishedMetadataV2(id),
    });
  }, []);

  const updatedv2AppData = useCallback(() => {
    let updatedProject: any = getSelectedTemplate(true);
    updatedProject = {
      ...updatedProject,
      v3: { ...updatedProject.v3, UI: previewStore.state.previewAppList },
    };
    return updatedProject;
  }, []);

  // 选择app
  const onSelectPreviewApp = useCallback((option: PreviewApp.UISaasValueType) => {
    // TODO 设置被选中的数据
    previewAppStore.previewToSaasUiData(option.id);
    previewAppStore.setShowPreviewPanel(true);
    previewAppStore.setPreviewShowStatus(true);
  }, []);

  const disabledPublish = useMemo(() => {
    // bsf-4912 replace thoughts
    return PreviewAppUtil.isDisabledPublish({
      saasUIData: saasUIData,
      isStaticMode: !!isStaticApp,
      thoughts: saasUIData.output,
      selectedUI: previewStore.state.selectedUI,
      nodes: creatorNodesStore.getNodes(),
    });
  }, [saasUIData, isStaticApp, creatorPreviewState.selectedUI]);

  const disabledPublishV2 = useMemo(() => {
    // bsf-4912 replace thoughts
    return PreviewAppUtil.isDisabledPublishV2({
      saasUIData: saasUIData,
      isStaticMode: !!isStaticApp,
      thoughts: saasUIData.output,
      selectedUI: previewStore.state.selectedUI,
      nodes: creatorNodesStore.getNodes(),
    });
  }, [saasUIData, isStaticApp, creatorPreviewState.selectedUI]);

  const disabledPublishByInput = useMemo(() => {
    return PreviewAppUtil.isDisabledPublishByInput({
      saasUIData: saasUIData,
      isStaticMode: !!isStaticApp,
      thoughts: saasUIData.output,
      selectedUI: previewStore.state.selectedUI,
      nodes: creatorNodesStore.getNodes(),
    });
  }, [saasUIData, isStaticApp, creatorPreviewState.selectedUI]);

  return {
    /**
     * 是否开启 previewAPP 功能
     */
    showDeleteTipSignal,
    /**
     * 是否禁用 publish
     */
    disabledPublish,
    disabledPublishV2,
    disabledPublishByInput,
    appStates,

    // publish flow v2 版本为 previewPanel 提供的四个panel 显示状态
    isInHistory,

    addPreview,

    /**
     * @deprecated `previewAppStore.saasUiDataToPreview`
     */
    saasUiDataToPreview: previewAppStore.saasUiDataToPreview,

    onSelectPreviewApp,
    /**
     * - 如果没有开启了v2 preview 权限,直接返回当前项目的 published_metadata
     * - 如果传入 id 且开启 v2 preview 权限，获取对应ID的 published_metadata
     * - 如果没有ID，且开启了v2 preview 默认使用当前activeID的 published_metadata
     */
    getPreviewAppActiveLable,
    removeByNodeIds,

    /**
     * 扩展 appId 或 publish_metadata
     *
     * - 如果没有开启 v2 preview 权限则原样返回
     * - 如果传入 appID 则使用传入id,没有传入id 则使用当前状态的id
     * - 如果传入 publish_metadta 则使用,否则原样返回
     *
     * @deprecated `previewAppStore.assignPreviewAppData`
     */
    assignPreviewAppData: previewAppStore.assignPreviewAppData,
    /**
     * 更新置顶preview 中的v2app 字段
     */
    updatePreviewV2App,

    // 删除其中一个数据
    deletePreviewV2App,
    updatedv2AppData,
    /**
     * preview app 删除节点触发方法
     * - 如果开启 preview v2 会弹窗拦截
     * - 如果没有开启 previwe v2 直接调用 onSuccess
     */
    triggerRemovePreviewAppNode,
    // chargeOptionUpdatedSignal,
    previewAppActivePublishMetadataV2Item,
  };
}

export const { Provider: PreviewAppProvider, useContainer: usePreviewAppStore } = createContainer(usePreviewApp);
